import request from 'api/requestUtils'
import qs from 'qs'
import { sortBy } from 'lib/array/arrayUtils'
import { filterObject } from 'lib/object/objectUtils'
import { isLeMd5PlaceId } from 'lib/search/placeIdUtils'
import { shouldSkipPlaceSlugLookup } from 'lib/search/shouldSkipPlaceSlugLookup'
import { slugify, isSlug } from 'lib/string/stringUtils'
import type { definitions, paths } from '@luxuryescapes/contract-search'
import { tourV2PopularDestinationMap } from './mappers/TourV2/tourV2SearchMapper'
import switchFunc from 'lib/function/switchFunc'
import { mapSpecificPlace } from './mappers/Search/placeMap'
import type { OfferListResult, TourSearchResultResponse } from './offerList.types'
import { mapSearchResultToOfferListMetaData } from 'api/mappers/hotelOfferMap'
import { mapPaymentOptionLabel } from 'lib/offer/filtersUtils'

type TypeaheadSearchResult = {
  format: {
    mainText: string
    secondaryText?: string
    imageId?: string
  }
  destinationType?: string
  id: string
  name: string
  searchType: 'destination' | 'landmark' | 'property' | 'experience' | 'departurePort' | 'airport' | 'tour' | 'bounds' | 'address'
  offerId?: string
  country_code?: string | null
  lat?: number
  lng?: number
}

export interface Destination {
  name: string
  place_id: string
}

// The airport code are the 3 characters after the open paren
function getAirportCode(str: string) {
  const openParenIndex = str.indexOf('(')
  return openParenIndex === -1 ? '' : str.substring(openParenIndex + 1, openParenIndex + 4)
}

function mapTypeaheadSuggestion(suggestion: TypeaheadSearchResult): App.SearchItem {
  const baseItem = {
    searchType: suggestion.searchType,
    value: suggestion.id,
    format: {
      mainText: suggestion.format.mainText,
      secondaryText: suggestion.format.secondaryText,
      imageId: suggestion.format?.imageId,
    },
    countryCode: suggestion.country_code,
  }

  // Handle discriminated union properly based on searchType
  switch (suggestion.searchType) {
    case 'destination':
      return {
        ...baseItem,
        searchType: 'destination',
        destinationType: suggestion.destinationType,
      }

    case 'property':
      return {
        ...baseItem,
        searchType: 'property',
        offerId: suggestion.offerId,

      }

    case 'airport':
      return {
        ...baseItem,
        searchType: 'airport',
        airportCode: getAirportCode(suggestion.format.mainText),
      }

    case 'landmark':
      return {
        ...baseItem,
        searchType: 'landmark',
      }

    case 'experience':
      return {
        ...baseItem,
        searchType: 'experience',
      }

    case 'departurePort':
      return {
        ...baseItem,
        searchType: 'departurePort',
      }

    case 'tour':
      return {
        ...baseItem,
        searchType: 'tour',
      }

    case 'bounds':
      return {
        ...baseItem,
        searchType: 'bounds',
      }

    case 'address':
      return {
        ...baseItem,
        searchType: 'address',
        lat: suggestion.lat,
        lng: suggestion.lng,
      }

    default:
      // Fallback for any unhandled types
      return {
        ...baseItem,
        searchType: suggestion.searchType,
      }
  }
}

export interface TypeaheadSuggestionsGetOptions {
  types?: Array<App.SearchPlaceType>
  searchTypes?: Array<App.SearchItem['searchType']>
  vertical?: App.SearchPlaceVertical
  priority?: App.SearchPlacePriority
  limit?: number
  canViewLuxPlusBenefits?: boolean
  rollout?: Array<string>
}
export async function getTypeaheadSuggestions(
  input: string,
  region: string,
  options: TypeaheadSuggestionsGetOptions,
): Promise<Array<App.SearchItem>> {
  const params = {
    region,
    type: options.types,
    vertical: options.vertical,
    ...(options.priority && { priority: options.priority }),
    limit: options.limit,
    isLuxPlusMember: options.canViewLuxPlusBenefits,
    rollout: options.rollout,
  }
  const queryString = qs.stringify(params)

  const typeaheadSuggestions = await request.get<App.ApiResponse<Array<TypeaheadSearchResult>>>(
    `/api/search/typeahead/${encodeURIComponent(input)}?${queryString}`,
  ).then(response => response.result.map(mapTypeaheadSuggestion))

  return options.searchTypes?.length ? typeaheadSuggestions.filter(s => options.searchTypes!.includes(s.searchType)) : typeaheadSuggestions
}

type GetPlaceBySlugResult = paths['/api/search/place/v1/slug-lookup/{slug}']['get']['responses']['200']['content']['application/json']['result']
export function getPlaceBySlug(input: string): Promise<App.Place> {
  // Avoid hitting svc-search for known-invalid scraper garbage (e.g. "jiaoxi, w_1200").
  if (shouldSkipPlaceSlugLookup(input)) {
    return Promise.reject(new Error('Invalid place slug'))
  }

  return request.get<App.ApiResponse<GetPlaceBySlugResult>>(`/api/search/place/v1/slug-lookup/${input}`).then(response => ({
    id: response.result.placeId,
    name: response.result.name,
    canonicalName: response.result.nameFull,
    type: response.result.type as App.PlaceType,
  }))
}

type GetSpecificPlacesResult = paths['/api/search/place/v1/specific-places/{id}']['get']['responses']['200']['content']['application/json']['result']
export function getSpecificPlaces(placeId: string): Promise<Array<App.SpecificPlace>> {
  return request.get<App.ApiResponse<GetSpecificPlacesResult>>(`/api/search/place/v1/specific-places/${placeId}`).then(response => response.result.map(mapSpecificPlace))
}

type GetDestinationImageIdResult = paths['/api/search/place/v1/places/{placeId}/image']['get']['responses']['200']['content']['application/json']['result']
export function getDestinationImageId(placeId: string): Promise<GetDestinationImageIdResult> {
  return request.get<App.ApiResponse<GetDestinationImageIdResult>>(`/api/search/place/v1/places/${placeId}/image`).then(response => response.result)
}

interface ServerPlaceTwo {
  lePlaceId: string
  lePlaceName: string
}

export function getPlaceByLatLong(latitude: number, longitude: number, level: App.PlaceType = 'city'): Promise<App.Place> {
  return request.get<App.ApiResponse<ServerPlaceTwo>>(`/api/search/place/v1/type/${level}/lat/${latitude}/lng/${longitude}`).then(response => {
    return {
      id: response.result.lePlaceId,
      name: response.result.lePlaceName.split(',')[0],
      canonicalName: response.result.lePlaceName,
    }
  })
}

type GetPlaceByidResult = paths['/api/search/place/v1/places/{id}']['get']['responses']['200']['content']['application/json']['result']
export function getPlaceById(lePlaceId: string): Promise<App.Place | undefined> {
  if (lePlaceId.includes(',')) {
    return Promise.resolve(undefined)
  }

  return request.get<App.ApiResponse<GetPlaceByidResult>>(`/api/search/place/v1/places/${lePlaceId}?include=boundingRadius`).then(response => {
    const br = response.result.boundingRadius
    return {
      id: response.result.placeId,
      name: response.result.name,
      canonicalName: response.result.nameFull,
      type: response.result.type as App.PlaceType,
      countryCode: response.result.countryCode,
      ...(br?.centre?.coordinates && {
        boundingRadius: {
          centre: {
            type: br.centre.type,
            longitude: br.centre.coordinates[0],
            latitude: br.centre.coordinates[1],
          },
          radius: br.radius,
        },
      }),
    }
  })
}

type BatchLePlaceDetails =
  paths['/api/search/place/v1/places']['get']['responses']['200']['content']['application/json']['result']
export async function getBatchPlaceById(lePlaceIds: Array<string>): Promise<Array<App.Place>> {
  // BE splits on comma; repeated ?ids=a&ids=b returns 400.
  const query = qs.stringify({ ids: lePlaceIds }, { arrayFormat: 'comma' })

  const response = await request.get<App.ApiResponse<BatchLePlaceDetails>>(`/api/search/place/v1/places?${query}`)
  return response.result.map((place) => ({
    id: place.placeId,
    name: place.name,
    canonicalName: place.nameFull,
    location: place.location,
    type: place.type as App.PlaceType,
    countryCode: place.countryCode,
  }))
}

export async function getPlaceBoundingRadius(lePlaceId: string): Promise<App.Place> {
  const query = qs.stringify({
    ids: lePlaceId,
    include: 'boundingRadius',
  }, { indices: false })

  const response = await request.get<App.ApiResponse<Array<GetPlaceByidResult>>>(`/api/search/place/v1/places?${query}`)
  return {
    id: response.result[0].placeId,
    name: response.result[0].name,
    canonicalName: response.result[0].nameFull,
    countryCode: response.result[0].countryCode,
    type: response.result[0].type as App.PlaceType,
    ...(response.result[0].boundingRadius && {
      boundingRadius: {
        centre: {
          type: response.result[0].boundingRadius.centre.type,
          longitude: response.result[0].boundingRadius.centre.coordinates[0],
          latitude: response.result[0].boundingRadius.centre.coordinates[1],
        },
        radius: response.result[0].boundingRadius.radius,
      },
    }),
  }
}

const sortPlaceTypeValue = switchFunc<number, App.PlaceType>({
  city: 1,
  multi_city_vicinity: 2,
  province_state: 3,
  country: 4,
  continent: 5,
  high_level_region: 6,
},
Number.MAX_SAFE_INTEGER)

type GetAncestorsByPlaceByIdResult = paths['/api/search/place/v1/places/{id}/ancestors']['get']['responses']['200']['content']['application/json']['result']
export function getPlaceAncestorsById(placeId: string): Promise<Array<App.Place>> {
  return request.get<App.ApiResponse<GetAncestorsByPlaceByIdResult>>(`/api/search/place/v1/places/${placeId}/ancestors?include=boundingRadius`).then(response => {
    const places = response.result.map(place => {
      const br = place.boundingRadius
      return {
        id: place.placeId,
        name: place.name,
        canonicalName: place.nameFull,
        type: place.type as App.PlaceType,
        countryCode: place.countryCode,
        ...(br?.centre?.coordinates && {
          boundingRadius: {
            centre: {
              type: br.centre.type,
              longitude: br.centre.coordinates[0],
              latitude: br.centre.coordinates[1],
            },
            radius: br.radius,
          },
        }),
      }
    })

    return sortBy(places, place => sortPlaceTypeValue(place.type), 'asc')
  })
}

export function getPopularDestinations(type: 'domestic' | 'international', regionCode: string): Promise<Array<App.PopularDestination>> {
  return request.get<App.ApiResponse<Array<App.PopularDestination>>>(`/api/search/popular-destinations/${type}?region=${regionCode}`)
    // Show destinations with images first
    .then(response => sortBy(response.result, destination => !!destination.imageId, 'desc'))
}

export function getBreadcrumbs(propertyId: string, type: 'le' | 'bedbank', showAllLocations: boolean): Promise<Array<Destination>> {
  return request.get<App.ApiResponse<Array<Destination>>>(`/api/search/locations/breadcrumbs/${propertyId}?withPlaceId=true&type=${type}&showAllLocations=${showAllLocations}`).then(result => result.result ?? [])
}

export function getAlternativeOffers(propertyId: string, type: 'le' | 'bedbank', region: string): Promise<App.AlternativeOffers> {
  return request.get<App.ApiResponse<App.AlternativeOffers>>(`/api/search/search-alternative-offers/${propertyId}?region=${region}&type=${type}`).then(result => result.result)
}

type RecommendationSuggestionResponse = paths['/api/search/hotel/v1/recommendations/suggestions']['get']['responses']['200']['content']['application/json']['result'][number]

const VALID_LOCATION_SEARCH_TYPES = new Set<App.ExtractedLocationParams['searchType']>(['destination', 'landmark', 'latLong'])

function toLocationSearchType(value: string | undefined): App.ExtractedLocationParams['searchType'] {
  if (value && VALID_LOCATION_SEARCH_TYPES.has(value as App.ExtractedLocationParams['searchType'])) {
    return value as App.ExtractedLocationParams['searchType']
  }
  return undefined
}

function mapRecommendationSuggestion(item: RecommendationSuggestionResponse): App.SearchItemV3 {
  const { location, ...rest } = item.searchItem.extractedData
  return {
    searchType: 'searchItemV3',
    value: item.suggestion,
    format: { mainText: item.suggestion },
    display: { mainText: item.suggestion, searchType: 'naturalLanguage' },
    searchItem: {
      typeaheadId: '',
      extractedData: {
        ...rest,
        placeIds: location?.placeIds ?? [],
        searchType: toLocationSearchType(location?.searchType),
      },
      extracting: false,
    },
  }
}

type RecommendationSuggestionsParams = paths['/api/search/hotel/v1/recommendations/suggestions']['get']['parameters']['query']

export function getRecommendationSuggestions(
  region: string,
  placeIds: Array<string>,
): Promise<Array<App.SearchItemV3>> {
  const lePlaceIds = placeIds.filter(isLeMd5PlaceId)

  if (lePlaceIds.length === 0) {
    return Promise.resolve([])
  }

  const params: Omit<RecommendationSuggestionsParams, 'brand'> = {
    region: region as RecommendationSuggestionsParams['region'],
    placeIds: lePlaceIds.join(','),
    numberOfSuggestions: 3,
  }
  return request.get<App.ApiResponse<Array<RecommendationSuggestionResponse>>>(
    `/api/search/hotel/v1/recommendations/suggestions?${qs.stringify(params)}`,
  ).then(response => response.result.map(mapRecommendationSuggestion))
}

export function reqToursPopularDestinations(regionCode: string) {
  return request.get<App.ApiResponse<Array<definitions['toursSearchTrendingDestinations']>>>(`/api/search/tour/v1/trending-destinations?region=${regionCode}`).then(result => result.result.map(tourV2PopularDestinationMap))
}

type GetTourFacetsParams = {
  placeId: string
  region: string
  category?: string
  excludeIds?: string
  luxPlusExclusive?: Array<string>
  agentHubExclusive?: Array<string>
  priceGte?: number
  priceLte?: number
  onSale?: boolean
  flashOffer?: boolean
  tourLengthGte?: number
  tourLengthLte?: number
  tourStyles?: Array<string>
  campaigns?: Array<string>
}
type GetTourFacetsResponse = paths['/api/search/tour/v1/facet/list']['get']['responses']['200']['content']['application/json']['result']

interface TourFacetV2Item {
  slug: string
  label: string
  count: number
}

interface TourFacetsV2 {
  operators: Array<TourFacetV2Item>
  locations: Array<TourFacetV2Item>
  groupTypes: Array<TourFacetV2Item>
  offerTypes: Array<TourFacetV2Item>
  categories: Array<TourFacetV2Item>
  offerTags: Array<TourFacetV2Item>
  tourStyles: Array<TourFacetV2Item>
  luxPlusExclusive: Array<TourFacetV2Item>
  agentHubExclusive: Array<TourFacetV2Item>
  campaigns: Array<TourFacetV2Item>
}

function mapTourFacetV2Items(items: Array<TourFacetV2Item>): Array<App.Tours.SearchFacetOption> {
  return items.map((item) => ({ label: item.label, value: item.slug, count: item.count }))
}

export async function getTourFacets(params: GetTourFacetsParams): Promise<App.Tours.TourSearchFacets> {
  const normalisedParams = {
    ...params,
    category: params.category && !isSlug(params.category) ? slugify(params.category) : params.category,
    tourStyles: normaliseTourFilterSlugs(params.tourStyles),
    campaigns: params.campaigns,
  }
  const response = await request.get<App.ApiResponse<GetTourFacetsResponse & { filtersV2: TourFacetsV2 }>>(`/api/search/tour/v1/facet/list?${qs.stringify(normalisedParams)}`)
  const { filtersV2 } = response.result
  return {
    operators: mapTourFacetV2Items(filtersV2.operators),
    locations: mapTourFacetV2Items(filtersV2.locations),
    groupTypes: mapTourFacetV2Items(filtersV2.groupTypes),
    offerTypes: mapTourFacetV2Items(filtersV2.offerTypes),
    categories: mapTourFacetV2Items(filtersV2.categories),
    offerTags: mapTourFacetV2Items(filtersV2.offerTags),
    tourStyles: mapTourFacetV2Items(filtersV2.tourStyles),
    luxPlusExclusive: mapTourFacetV2Items(filtersV2.luxPlusExclusive),
    agentHubExclusive: mapTourFacetV2Items(filtersV2.agentHubExclusive),
    campaigns: mapTourFacetV2Items(filtersV2.campaigns),
    tourLength: {
      min: response.result.tourLength.min,
      max: response.result.tourLength.max,
    },
    numberOfPlacesVisited: {
      min: response.result.numberOfPlacesVisited.min,
      max: response.result.numberOfPlacesVisited.max,
    },
    price: {
      min: response.result.price.min,
      max: response.result.price.max,
    },
  }
}

export function hasValidLength(data?: Array<any> | string): boolean {
  return !!data && data.length > 0
}

export function isNotDefaultSort(sortBy?: string): boolean {
  return !!sortBy && sortBy !== 'recommended' && sortBy !== 'homepage' && sortBy !== 'vertical-tour'
}

function normaliseTourFilterSlugs(values?: Array<string>): Array<string> | undefined {
  if (!values?.length) return undefined
  return values.map(v => isSlug(v) ? v : slugify(v))
}

export function getTourSearchListByFacet(params: App.OfferListFilters, region: string) {
  const queryParamsObj = {
    region,
    placeIds: hasValidLength(params.destinations) ? [...params.destinations!] : [params.destinationId],
    priceGte: params.priceGte,
    priceLte: params.priceLte,
    operators: normaliseTourFilterSlugs(params.operators),
    tourLengthGte: params.tourLengthGte,
    tourLengthLte: params.tourLengthLte,
    numberOfPlacesVisitedGte: params.numberOfPlacesVisitedGte,
    numberOfPlacesVisitedLte: params.numberOfPlacesVisitedLte,
    isFacet: 'true',
    offerType: 'tour_v2',
    sortBy: isNotDefaultSort(params.sortBy) ? params.sortBy : 'vertical',
    flexibleMonths: hasValidLength(params.flexibleMonths) ? params.flexibleMonths : undefined,
    flexibleNights: params.flexibleNights,
    isSmallGroup: params.isSmallGroup,
    isLargeGroup: params.isLargeGroup,
    isFamilyFriendly: params.isFamilyFriendly,
    isPrivateRequest: params.isPrivateRequest,
    productTypes: params.productTypes,
    categories: normaliseTourFilterSlugs(params.categories),
    onSale: params.onSale,
    flashOffer: params.flashOffer,
    luxPlusExclusive: params.luxPlusFeatures,
    agentHubExclusive: params.agentHubFeatures,
    isLuxPlusMember: params.isLuxPlusMember,
    showEnrichedResult: params.showEnrichedResult,
    tourStyles: normaliseTourFilterSlugs(params.tourStyles),
    agentHubExclusiveOffersOnly: params.agentHubExclusiveOffersOnly,
    campaigns: params.campaigns?.join(','),
    limit: params.limit,
  }

  const filteredQueryParams = filterObject(queryParamsObj, (_key, value) => value !== undefined && value !== null && value !== '')
  const queryString = qs.stringify(filteredQueryParams, { arrayFormat: 'indices' })

  return request.get<TourSearchResultResponse>(`/api/search/tour/v1/list?${queryString}`)
}

export async function getPopularHotelFilters(region: string) {
  const query = {
    region,
    limit: 40,
  }
  return request.get<App.ApiResponse<definitions['popularFilterType']>>(`/api/search/hotel/v1/popular/filters?${qs.stringify(query)}`).then(res => res.result)
}

type GetGoogleAutocompleteTypes = paths['/api/search/place/v1/google/autocomplete/{input}']['get']
export type GoogleAutocompletePlaceItem = GetGoogleAutocompleteTypes['responses']['200']['content']['application/json']['result'][number]
export async function getAutocompleteSuggestions(input: string, query: Omit<GetGoogleAutocompleteTypes['parameters']['query'], 'brand'> = {}): Promise<Array<GoogleAutocompletePlaceItem>> {
  const queryString = qs.stringify(query)

  return request.get<App.ApiResponse<Array<GoogleAutocompletePlaceItem>>>(`/api/search/place/v1/google/autocomplete/${encodeURIComponent(input)}?${queryString}`)
    .then(response => response.result)
}

type GetGooglePlaceDetailsTypes = paths['/api/search/place/v1/google/place-details/{placeId}']['get']
export type GooglePlaceDetails = GetGooglePlaceDetailsTypes['responses']['200']['content']['application/json']['result']

export async function getGooglePlaceDetails(googlePlaceId: string): Promise<GooglePlaceDetails> {
  return request.get<App.ApiResponse<GooglePlaceDetails>>(`/api/search/place/v1/google/place-details/${googlePlaceId}`)
    .then(response => response.result)
}

type SearchLightResponse = paths['/api/search/v1/light']['get']['responses']['200']['content']['application/json']
type SearchLightParameters = paths['/api/search/v1/light']['get']['parameters']['query']
type SearchLightRegion = paths['/api/search/v1/light']['get']['parameters']['query']['region']

export async function getSearchLight({
  placeId,
  region,
  limit,
  holidayTypes,
  propertyTypes,
  offerTypes,
  scheduleVisibilities,
  luxPlusExclusive,
  campaigns,
  channelMarkup,
  rollout,
  loyalty,
}: {
  placeId: string
  region: string
  limit?: number
  holidayTypes?: Array<string>
  propertyTypes?: Array<string>
  offerTypes?: Array<App.OfferListOfferType>
  scheduleVisibilities?:
  Array<'listVisible' | 'onlineVisible' | 'spoofVisible' | 'luxPlusEarlyAccessVisible' | 'luxPlusExclusiveVisible'>
  luxPlusExclusive?: Array<App.LuxPlusFeatureFilter>
  channelMarkup?: App.ChannelMarkup
  campaigns?: Array<string>
  rollout?: Array<string>
  loyalty?: Array<string>
}): Promise<OfferListResult> {
  const params: (Omit<SearchLightParameters, 'brand'>) = {
    region: region as SearchLightRegion,
    placeIds: placeId,
    limit,
    holidayTypes: holidayTypes?.join(',') || undefined,
    propertyTypes: propertyTypes?.join(',') || undefined,
    offerType: offerTypes?.join(',') || undefined,
    scheduleVisibilities: scheduleVisibilities?.join(',') || undefined,
    luxPlusExclusive: luxPlusExclusive?.join(',') || undefined,
    rollout: rollout?.join(',') || undefined,
    campaigns: campaigns?.join(',') || undefined,
    societe: loyalty?.join(',') || undefined,
  }
  const queryString = qs.stringify(params)

  return request.get<SearchLightResponse>(`/api/search/v1/light?${queryString}`).then(response => {
    const v2 = response.filtersV2

    return {
      searchId: response.searchId,
      offerIds: response.result.map(res => res.id),
      metaData: response.result.map(
        (item, index) =>
          mapSearchResultToOfferListMetaData(item, index, channelMarkup),
      ),
      filters: {
        amenities: v2.amenities,
        bedrooms: v2.bedrooms,
        campaigns: v2.campaigns,
        customerRatings: v2.customerRatings,
        holidayTypes: v2.holidayTypes,
        inclusions: v2.inclusions,
        propertyTypes: v2.propertyTypes ?? [],
        offerTypes: v2.type,
        luxPlusFeatures: v2.luxPlusExclusive ?? [],
        loyalty: v2.societe ?? [],
        agentHubFeatures: v2.agentHubExclusive ?? [],
        chains: v2.chains ?? [],
        paymentOptions: (v2.paymentOptions ?? []).map(mapPaymentOptionLabel),
        total: response.total,
      },
      filterOrder: response.filterOrderV2,
      offerCount: response.total,
      searchVertical: 'hotels',
    }
  })
}

export async function getSearchTags(_options: { propertyId: string }): Promise<{ tags: Array<string> }> {
  // svc-search does not expose property tags; keep callers on the existing no-tags path without issuing a 404ing request.
  return { tags: [] }
}

type NearbyResponse = paths['/api/search/hotel/v1/properties/{propertyId}/nearby']['get']['responses']['200']['content']['application/json']

export async function getPropertyNearby(propertyId: string): Promise<App.NearbyByCategory> {
  const url = `/api/search/hotel/v1/properties/${encodeURIComponent(propertyId)}/nearby?categories=beach`
  try {
    const response = await request.get<NearbyResponse>(url, { excludeBrand: true })
    return response.result?.nearby ?? {}
  } catch(error) {
    if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
      return {}
    }
    throw error
  }
}

// Note: This endpoint is not in the contract yet, using App.UserRecentSearch domain types
export async function getUserRecentSearches(
  options: { leUserId?: string, domainUserId?: string },
): Promise<Array<App.UserRecentSearch>> {
  const query = qs.stringify(filterObject(options, (_, value) => !!value))
  const response = await request.get<App.ApiResponse<Array<App.UserRecentSearch>>>(
    `/api/search/hotel/v1/recent-searches?${query}`,
    { excludeBrand: true },
  )
  return response.result
}
